import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import { find, groupBy, propEq } from 'ramda';
import { ErrorHandlerService } from '../core/error-handler';
import { UserService } from '../core/user';
import { UserLocation } from '../models/user/location.model';
import { AnalyticsService } from '../core/analytics';
import { environment } from 'spotlight-ui/environments/environment';
import { AutomationSource } from './dependencies/automationsSource';
import {
  DigitalMarketingAutomationSource
} from './dependencies/digital-marketing-automation-source';
import { DiscoverConstants } from './discover.constants';
import { AutomationCustomerCount } from '../models/discover/automation.model';
import { Observable, of, throwError, timer } from 'rxjs';

@Injectable()
export class DiscoverService {
  constructor(
    private us: UserService,
    private http: HttpClient,
    private ehs: ErrorHandlerService,
    private analytics: AnalyticsService
  ) {

  }

  private readonly automationsSource: AutomationSource = new DigitalMarketingAutomationSource(this.http);
  private readonly audienceCache = new Map<string, { value: AutomationCustomerCount; expiresAt: number }>();
  private readonly audienceCacheTtlMs = 10 * 60 * 1000;
  private readonly staleAudienceTileIds = new Set<string>();
  private readonly recentlySentCustomerIds = new Map<string, Set<string>>();
  private readonly audienceMaxRetries = 3;
  private readonly audienceRetryBaseDelayMs = 1000;

  public findById(id, tiles) {
    let hasId = propEq('id', id);
    let tile = find(hasId, tiles);
    return tile && tile.enabled && tile.enabled === 'true';
  }

  public getTileById(id, tiles) {
    let hasId = propEq('id', id);
    let tile = find(hasId, tiles);
    return (tile && tile.enabled && tile.enabled === 'true') ? tile : null;
  }

  public loadDiscoverTiles(featureMap) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/discover/tiles`;
        return this.http.get(environment.spotlightApiUrl + url).pipe(
          switchMap((res: any) =>{
            return this.getDMSTiles(locationNumber, res, featureMap)
          }),
          map(( [discoverTiles, dmsAutomations, featureMap ]: [any[], any[], any]) => {
            return this.mergeDynamoAndDMSTiles(discoverTiles, dmsAutomations, featureMap)
          })
        );
      }),
      catchError((err) => this.ehs.handleError(err))
    );
  }

  public loadChurnCustomers() {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        let url = `/api/location/${locationNumber}/churnCustomers`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      map((res) => ({ id: 'churn-flag', ...res })),
      catchError((error) => this.ehs.handleError(error))
    );
  }

  public loadFirstTimeCustomers() {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/firstTimeMobile`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      map((res) => ({ id: 'engage-first', ...res })),
      catchError((error) => this.ehs.handleError(error))
    );
  }

  public markAudiencesStale(tileIds: string[]): void {
    tileIds.forEach((tileId) => this.staleAudienceTileIds.add(tileId));
  }

  public takeStaleAudienceTileIds(): string[] {
    const tileIds = [...this.staleAudienceTileIds];
    this.staleAudienceTileIds.clear();
    return tileIds;
  }

  public recordSentCustomers(templateId: string, customerIds: unknown[]): AutomationCustomerCount | null {
    const ids = this.normalizeCustomerIds(customerIds);
    if (!templateId || ids.length === 0) {
      return null;
    }
    const locationNumber = this.selectedLocationNumber();
    if (!locationNumber) {
      return null;
    }
    const key = this.locationTemplateKey(locationNumber, templateId);
    const sent = this.recentlySentCustomerIds.get(key) ?? new Set<string>();
    ids.forEach((id) => sent.add(id));
    this.recentlySentCustomerIds.set(key, sent);

    const entry = this.audienceCache.get(key);
    if (!entry) {
      return null;
    }
    const updated = this.excludeSentCustomers(key, entry.value, false);
    this.audienceCache.set(key, { ...entry, value: updated });
    return updated;
  }

  public clearAutomationAudienceCache(templateId?: string): void {
    if (!templateId) {
      this.audienceCache.clear();
      return;
    }
    for (const key of this.audienceCache.keys()) {
      if (key.endsWith(`:${templateId}`)) {
        this.audienceCache.delete(key);
      }
    }
  }

  public loadAutomationAudienceByTemplateId(id: string, bypassCache = false) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const cacheKey = this.locationTemplateKey(loc.locationNumber, id);
        if (!bypassCache) {
          const cached = this.audienceCache.get(cacheKey);
          if (cached && cached.expiresAt > Date.now()) {
            return of(cached.value);
          }
          if (cached) {
            this.audienceCache.delete(cacheKey);
          }
        }
        return this.fetchAudienceWithRetry(loc.locationNumber, id).pipe(
          map((result) => this.excludeSentCustomers(cacheKey, result, true)),
          tap((result) => {
            this.audienceCache.set(cacheKey, {
              value: result,
              expiresAt: Date.now() + this.audienceCacheTtlMs
            });
          })
        );
      }),
      catchError((error)=> this.ehs.handleError(error))
    )
  }

  public loadLapsedBreakfastCustomers() {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/lapsedBreakfast/customers`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      map((res) => ({ id: 'grow-breakfast', ...res })),
      catchError((error) => this.ehs.handleError(error))
    );
  }

  public loadGrowDriveThruCustomers() {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/cluster/customer/driveThru`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      map((res) => ({ id: 'grow-drive-thru-destination', ...res })),
      catchError((error) => this.ehs.handleError(error))
    );
  }

  public loadGrowCurbsideCustomers() {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/cluster/customer/curbSide`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      map((res) => ({ id: 'grow-curbside-destination', ...res })),
      catchError((error) => this.ehs.handleError(error))
    );
  }

  public loadMaximizeRedemption() {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/treatRedemption`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      map((res) => ({ id: 'maximize-redemption', ...res })),
      catchError((error) => this.ehs.handleError(error))
    );
  }

  public loadOldReward() {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/old/customers`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      map((res) => ({ id: 'operator-led-delivery', ...res })),
      catchError((error) => this.ehs.handleError(error))
    );
  }

  public loadFilters(tiles) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/discoverFilters`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => {
        const tilesById = groupBy((t: any) => t.id)(tiles);
        return {
          ...tilesById,
          ...res.responseObject
        };
      }),
      catchError((error) => this.ehs.handleError(error))
    );
  }

  public stopAutomationById(payload, featureMap) {
    this.analytics.sendEvent(
      'Automation Stopped',
      payload
    );

    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const locationNumber = loc.locationNumber;
        let url: string;
        if ( featureMap[payload.id] != undefined && featureMap[payload.id] && payload.fieldId !== '' ) {
          return this.automationsSource.stopAutomation(locationNumber, payload.id, payload.fieldId )

        }
        if (payload.fieldId !== '') {
          url = `/api/location/${locationNumber}/automation/${payload.id}?fieldId=${payload.fieldId}`;
        } else {
          url = `/api/location/${locationNumber}/automation/${payload.id}`;
        }

        return this.http.delete(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject ?? res ?? []),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  public stopThirdPartyPromotionById(payload) {
    this.analytics.sendEvent(
      'Third Party Promo Stopped',
      { 'Third Party Promo Id': payload.id }
    );
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/thirdPartyPromotion/${payload.id}`;
        return this.http.delete(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  public stopFreeItemInCartById(payload) {
    this.analytics.sendEvent(
      'Free Item In Cart Stopped',
      { 'Free Item In Cart Id': payload.fieldId }
    );
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/freeInCart/${payload.fieldId}`;
        return this.http.delete(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  public stopBonusPointPromotionById(promotion) {
    this.analytics.sendEvent(
      'Bonus Point Promotion Stopped',
      { 'Bonus Point Promotion Id': promotion.payload.fieldId }
    );

    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const locationNumber = loc.locationNumber;
        const eventId = promotion.payload.fieldId;
        const url = `/api/location/${locationNumber}/automation/bonus-point?fieldId=${eventId}`;
        return this.http.delete(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject || []),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  public stopCommunityCareEventById(payload) {
    this.analytics.sendEvent(
      'Community Care Event Stopped',
      { 'Community Care Event Id': payload.eventId }
    );

    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const locationNumber = loc.locationNumber;
        const eventId = payload.eventId;
        const url = `/api/location/${locationNumber}/communityCareEvent/${eventId}`;
        return this.http.delete(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject || []),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  public markResolvedFailedCampaign(payload) {
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const request = {
          'campaignId' : payload.campaignId,
          'markResolved': true,
          'type': payload.type
        };
        const url = `/api/location/${locationNumber}/discover/error-tile/resolved`;
        return this.http.put(environment.spotlightApiUrl + url, request);
      }),
      map((res: any) => res.responseObject),
      catchError(this.ehs.handleError)
    );
  }

  private normalizeCustomerIds(customerIds: unknown[]): string[] {
    if (!Array.isArray(customerIds)) {
      return [];
    }
    return customerIds.map((customerId) => {
      if (typeof customerId === 'string') {
        return customerId;
      }
      if (customerId && typeof customerId === 'object') {
        const record = customerId as { cfaId?: string; id?: string };
        return record.cfaId || record.id || '';
      }
      return '';
    }).filter((customerId) => customerId.length > 0);
  }

  private locationTemplateKey(locationNumber: string, templateId: string): string {
    return `${locationNumber}:${templateId}`;
  }

  private selectedLocationNumber(): string | undefined {
    let locationNumber: string | undefined;
    this.us.getSelectedLocation().pipe(take(1)).subscribe((location: UserLocation) => {
      locationNumber = location?.locationNumber;
    });
    return locationNumber;
  }

  private excludeSentCustomers(
    cacheKey: string,
    value: AutomationCustomerCount,
    dropWhenAbsent: boolean
  ): AutomationCustomerCount {
    const sent = this.recentlySentCustomerIds.get(cacheKey);
    if (!sent?.size || !Array.isArray(value?.customers)) {
      return value;
    }
    const customers = value.customers.filter((customerId) => !sent.has(customerId));
    if (customers.length === value.customers.length) {
      if (dropWhenAbsent) {
        this.recentlySentCustomerIds.delete(cacheKey);
      }
      return value;
    }
    return {
      ...value,
      customers,
      count: customers.length,
      disableEngagement: customers.length === 0
    };
  }

  private fetchAudienceWithRetry(
    locationNumber: string,
    templateId: string,
    attempt = 0
  ): Observable<AutomationCustomerCount> {
    return this.automationsSource.getAutomationAudience(locationNumber, templateId).pipe(
      catchError((error: unknown) => {
        if (attempt < this.audienceMaxRetries && this.isRetryableAudienceError(error)) {
          const delayMs = this.audienceRetryBaseDelayMs * (2 ** attempt);
          return timer(delayMs).pipe(
            switchMap(() => this.fetchAudienceWithRetry(locationNumber, templateId, attempt + 1))
          );
        }
        // Notify downstream on a later turn. Failing from inside the request's
        // error callback makes the console report that same failure repeatedly.
        return timer(0).pipe(switchMap(() => throwError(() => error)));
      })
    );
  }

  private isRetryableAudienceError(error: unknown): boolean {
    return error instanceof HttpErrorResponse
      && (error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500);
  }

  private getDMSTiles(locationNumber, res, featureMap) {
    if ( Object.values(featureMap).some(Boolean)) {
      return this.automationsSource.getMappedAutomationTiles(locationNumber, featureMap).pipe(
        map((dmsAutomations) => [res.responseObject, dmsAutomations, featureMap])
      );
    } else {
      return of([res.responseObject, [], featureMap])
    }
  }

  private mergeDynamoAndDMSTiles(discoverTiles, dmsAutomations, featureMap) {
    let finalTiles = []
    discoverTiles.forEach(tile => {
      // Prefer DMS tile whenever the automations feature is on for this id, even if Dynamo
      // still marks the slot as automated — otherwise we never merge and ng-content stays hidden.
      if (featureMap[tile.id]) {
        const dmsTile = dmsAutomations.find((dmaAutomation) => dmaAutomation.id === tile.id);
        if (dmsTile) {
          const dmsDataStaleWhenTileAutomated =
            tile.automated === true && dmsTile.automated === false;
          if (!dmsDataStaleWhenTileAutomated) {
            // The "New!" upgrade tag is controlled by the Dynamo tile record (displayNewIcon),
            // not by the fact that the tile is served from DMS.
            const displayNewIcon = Boolean(tile.displayNewIcon);
            finalTiles.push({
              ...dmsTile,
              displayAutomationVersionUpgrade: displayNewIcon,
              ...(displayNewIcon
                ? { automationVersionUpgradeTooltip: DiscoverConstants.automationVersionUpgradeTooltip }
                : {})
            });
            return;
          }
        }
      }
      if (tile.automated) {
        finalTiles.push(tile);
      } else if (featureMap[tile.id]) {
        // Feature on but no DMS tile for this id — same as legacy: omit tile
      } else {
        finalTiles.push(tile);
      }
    })
    return finalTiles;
  }
}
